/* Mezkla collection page — vanilla JS.

   Handles:
     - Hero parallax + opacity dissolve
     - Scroll-reveal cascade (cards + editorials)
     - Quick-add from product card size buttons (POSTs /cart/add.js,
       dispatches cart:open + cart:updated for the drawer)
     - Infinite scroll (auto fetch + append as a sentinel nears the viewport)
     - Floating filter/sort pill with scroll-direction hide/show
     - Filter drawer (right-slide) with URL state sync
     - Sort drawer (right-slide) with auto-apply on radio change
     - Active filter chips below the pill
     - Browser back/forward sync (popstate)

   No framework, no dependencies. The pill listens to the `mezkla:scroll`
   event from header-scroll.js — no new scroll listener added by this file.
*/
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ─── Hero parallax + opacity dissolve ─────────────────────────────── */
  var parallaxEl = document.querySelector('[data-parallax]');
  if (parallaxEl && !reduceMotion) {
    var parallaxTicking = false;

    function updateParallax() {
      var scroll = window.scrollY;
      var heroH = window.innerHeight * 0.5;
      var progress = Math.min(1, scroll / heroH);
      var eased = progress * (2 - progress);
      var translateY = scroll * 0.4;
      var scale = 1 + eased * 0.06;
      var opacity = 1 - eased;
      parallaxEl.style.transform =
        'translate3d(0, ' + translateY + 'px, 0) scale(' + scale + ')';
      parallaxEl.style.opacity = String(opacity);
      parallaxTicking = false;
    }

    function onParallaxScroll() {
      if (!parallaxTicking) {
        requestAnimationFrame(updateParallax);
        parallaxTicking = true;
      }
    }

    updateParallax();
    window.addEventListener('scroll', onParallaxScroll, { passive: true });
  }

  /* ─── Scroll-reveal cascade (cards + editorials) ───────────────────── */
  var revealObserver;
  if ('IntersectionObserver' in window) {
    revealObserver = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        if (entry.isIntersecting) {
          entry.target.setAttribute('data-visible', 'true');
          revealObserver.unobserve(entry.target);
        }
      }
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
  }

  function observeReveal(scope) {
    var els = (scope || document).querySelectorAll('[data-reveal]:not([data-visible])');
    if (!revealObserver) {
      els.forEach(function (el) { el.setAttribute('data-visible', 'true'); });
      return;
    }
    els.forEach(function (el) { revealObserver.observe(el); });
  }

  observeReveal();

  /* ─── Quick-add per card (size pick → Quick add submits) ──────────────
     Sized products: click a size button → write its variant id into the
     hidden input, mark size active, enable the Quick add CTA. Quick add
     submits the form. We intercept submit, POST to /cart/add.js, and
     dispatch cart:open + cart:updated for the drawer. Single-variant
     products skip the size step entirely. */
  function bindSizeButtons(scope) {
    var forms = (scope || document).querySelectorAll('[data-product-form]');
    forms.forEach(function (form) {
      if (form.dataset.bound === '1') return;
      form.dataset.bound = '1';

      var hiddenInput = form.querySelector('[data-variant-input]');
      var sizeBtns = form.querySelectorAll('[data-size]');
      var cta = form.querySelector('[data-quick-add-cta]');

      sizeBtns.forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          if (btn.disabled) return;

          var variantId = btn.getAttribute('data-variant-id');
          if (hiddenInput) hiddenInput.value = variantId || '';

          sizeBtns.forEach(function (b) { b.setAttribute('data-active', 'false'); });
          btn.setAttribute('data-active', 'true');

          if (cta) cta.disabled = false;
        });
      });

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        e.stopPropagation();

        var variantId = hiddenInput ? hiddenInput.value : '';
        if (!variantId || (cta && cta.disabled)) return;

        if (cta) cta.disabled = true;

        var body = new FormData();
        body.append('id', variantId);
        body.append('quantity', '1');

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: body,
        })
          .then(function (r) {
            if (!r.ok) throw new Error('add-to-cart failed');
            return r.json();
          })
          .then(function () {
            document.dispatchEvent(new CustomEvent('cart:updated'));
            document.dispatchEvent(new CustomEvent('cart:open'));
            if (cta) cta.disabled = false;
          })
          .catch(function () {
            if (cta) cta.disabled = false;
            form.submit();
          });
      });
    });
  }

  bindSizeButtons();

  /* ─── Shared fetch / parse helpers ─────────────────────────────────── */
  function fetchHtml(url) {
    return fetch(url, { headers: { 'X-Requested-With': 'fetch' } })
      .then(function (r) {
        if (!r.ok) throw new Error('fetch failed');
        return r.text();
      });
  }

  function parseGridResponse(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var gridEl = doc.querySelector('[data-collection-grid]');
    var paginationEl = doc.querySelector('.collection-grid__pagination');
    var countEl = doc.querySelector('[data-product-count]');
    return {
      gridChildren: gridEl ? Array.prototype.slice.call(gridEl.children) : [],
      paginationHtml: paginationEl ? paginationEl.innerHTML : '',
      productCount: countEl ? parseInt(countEl.textContent, 10) : null
    };
  }

  /* ─── Infinite scroll (auto fetch + append) ─────────────────────────────
     Replaces the old "Show more" button. A sentinel div sits below the grid;
     as it nears the viewport we fetch the next page, append its products, and
     read the following page's URL from the freshly-fetched sentinel. When the
     server stops sending a sentinel (last page), we remove ours — so the grid
     simply ends at the final product with nothing after it. */
  var grid = document.querySelector('[data-collection-grid]');

  function insertShimmers(count) {
    if (!grid) return [];
    var shimmers = [];
    for (var i = 0; i < count; i++) {
      var s = document.createElement('div');
      s.className = 'shimmer-bg collection-grid__shimmer';
      s.setAttribute('aria-hidden', 'true');
      s.setAttribute('data-shimmer', 'true');
      grid.appendChild(s);
      shimmers.push(s);
    }
    return shimmers;
  }

  function removeShimmers() {
    if (!grid) return;
    grid.querySelectorAll('[data-shimmer]').forEach(function (s) { s.remove(); });
  }

  var sentinelObserver = null;
  var loadingNext = false;

  function loadNextPage(sentinel) {
    if (!sentinel || loadingNext) return;
    var url = sentinel.getAttribute('data-next-url');
    if (!url) return;

    loadingNext = true;
    sentinel.setAttribute('data-loading', 'true');
    insertShimmers(4);  // subtle "more coming" hint; real count arrives with the page

    fetchHtml(url)
      .then(parseGridResponse)
      .then(function (parsed) {
        removeShimmers();
        parsed.gridChildren.forEach(function (n) { grid.appendChild(n); });
        observeReveal(grid);
        bindSizeButtons(grid);

        // The next page's URL lives on the freshly-fetched sentinel.
        var doc = new DOMParser().parseFromString(parsed.paginationHtml, 'text/html');
        var nextSentinel = doc.querySelector('[data-infinite-sentinel]');
        if (nextSentinel && nextSentinel.getAttribute('data-next-url')) {
          sentinel.setAttribute('data-next-url', nextSentinel.getAttribute('data-next-url'));
          sentinel.removeAttribute('data-loading');
          loadingNext = false;
          // Re-arm: if the sentinel is still within range (user parked near the
          // bottom), the observer fires again and keeps loading page by page.
          if (sentinelObserver) sentinelObserver.observe(sentinel);
        } else {
          // Last page reached — drop the sentinel so the page ends cleanly.
          if (sentinelObserver) sentinelObserver.unobserve(sentinel);
          sentinel.remove();
        }
      })
      .catch(function () {
        removeShimmers();
        sentinel.removeAttribute('data-loading');
        loadingNext = false;
        // Leave the sentinel in place so a later scroll can retry.
        if (sentinelObserver) sentinelObserver.observe(sentinel);
      });
  }

  // (Re)attach the observer to whatever sentinel is currently in the DOM.
  // Called on first load and again after a filter/sort refresh swaps the grid.
  function armSentinel() {
    var s = grid ? document.querySelector('[data-infinite-sentinel]') : null;
    if (!s) return;
    if ('IntersectionObserver' in window) {
      if (!sentinelObserver) {
        sentinelObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              // Stop watching while this fetch runs; loadNextPage re-observes
              // when there's another page, or removes the sentinel when done.
              sentinelObserver.unobserve(entry.target);
              loadNextPage(entry.target);
            }
          });
        }, { rootMargin: '800px 0px' });  // start fetching ~one screen early
      }
      sentinelObserver.observe(s);
    } else {
      // No IntersectionObserver (very old browsers): pull the next page eagerly.
      loadNextPage(s);
    }
  }

  armSentinel();

  /* ─── Filter pill visibility ──────────────────────────────────────────
     Visible when body has nav-fixed (header in scrolled state) AND the
     user isn't at the bottom of the page. Hidden at the top of the hero
     and once the user reaches the end of the grid. */
  var pill = document.querySelector('[data-collection-pill]');

  function recalcPill() {
    if (!pill) return;
    var y = window.scrollY || 0;
    var winH = window.innerHeight;
    var docH = Math.max(
      document.documentElement.scrollHeight,
      document.body.scrollHeight
    );
    var atBottom = (y + winH) >= (docH - 100);
    var navFixed = document.body.classList.contains('nav-fixed');
    pill.setAttribute('data-visible', (navFixed && !atBottom) ? 'true' : 'false');
  }

  if (pill) {
    document.addEventListener('mezkla:scroll', recalcPill);
    window.addEventListener('resize', recalcPill);
    recalcPill();
  }

  /* ─── Drawer open/close (filter + sort) ────────────────────────────── */
  var drawerFilter = document.querySelector('[data-drawer="filter"]');
  var drawerSort = document.querySelector('[data-drawer="sort"]');

  function openDrawer(drawer) {
    if (!drawer) return;
    drawer.removeAttribute('hidden');
    void drawer.offsetWidth;  // force reflow so data-open transition fires
    drawer.setAttribute('data-open', 'true');
    document.body.style.overflow = 'hidden';
    var firstFocus = drawer.querySelector('[data-drawer-close]');
    if (firstFocus) firstFocus.focus();
  }

  function closeDrawer(drawer) {
    if (!drawer) return;
    drawer.setAttribute('data-open', 'false');
    document.body.style.overflow = '';
    var delay = reduceMotion ? 0 : 280;
    window.setTimeout(function () { drawer.setAttribute('hidden', ''); }, delay);
  }

  function isOpen(drawer) {
    return drawer && drawer.getAttribute('data-open') === 'true';
  }

  var pillFilter = document.querySelector('[data-pill-filter]');
  var pillSort = document.querySelector('[data-pill-sort]');
  if (pillFilter && drawerFilter) {
    pillFilter.addEventListener('click', function () { openDrawer(drawerFilter); });
  }
  if (pillSort && drawerSort) {
    pillSort.addEventListener('click', function () { openDrawer(drawerSort); });
  }

  document.querySelectorAll('[data-drawer-close]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var name = btn.getAttribute('data-drawer-close');
      closeDrawer(document.querySelector('[data-drawer="' + name + '"]'));
    });
  });
  document.querySelectorAll('[data-drawer-backdrop]').forEach(function (bd) {
    bd.addEventListener('click', function () {
      var name = bd.getAttribute('data-drawer-backdrop');
      closeDrawer(document.querySelector('[data-drawer="' + name + '"]'));
    });
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (isOpen(drawerFilter)) closeDrawer(drawerFilter);
    if (isOpen(drawerSort)) closeDrawer(drawerSort);
  });

  /* ─── Filter / sort URL building ───────────────────────────────────── */
  var filterForm = document.querySelector('[data-filter-form]');
  var sortForm = document.querySelector('[data-sort-form]');
  var collectionBaseUrl = filterForm
    ? filterForm.getAttribute('action')
    : window.location.pathname;
  var FILTER_KEYS = [
    'filter.p.tag',
    'filter.v.option.color',
    'filter.v.availability'
  ];

  function buildFilterUrl() {
    var params = new URLSearchParams();
    if (filterForm) {
      var fd = new FormData(filterForm);
      fd.forEach(function (value, key) {
        var s = String(value).trim();
        if (s !== '') params.append(key, s);
      });
    }
    var current = new URLSearchParams(window.location.search);
    if (current.has('sort_by')) params.append('sort_by', current.get('sort_by'));
    var qs = params.toString();
    return collectionBaseUrl + (qs ? '?' + qs : '');
  }

  function buildSortUrl(sortValue) {
    var params = new URLSearchParams(window.location.search);
    if (sortValue && sortValue !== 'manual') params.set('sort_by', sortValue);
    else params.delete('sort_by');
    var qs = params.toString();
    return collectionBaseUrl + (qs ? '?' + qs : '');
  }

  /* ─── Refresh grid (used by filter apply, sort change, popstate) ──── */
  function refreshGrid(url) {
    if (!grid) return Promise.resolve();
    grid.dataset.loading = 'true';

    return fetchHtml(url)
      .then(parseGridResponse)
      .then(function (parsed) {
        grid.innerHTML = '';
        parsed.gridChildren.forEach(function (n) { grid.appendChild(n); });
        grid.dataset.loading = 'false';
        observeReveal(grid);
        bindSizeButtons(grid);

        var paginationContainer = document.querySelector('.collection-grid__pagination');
        if (paginationContainer) {
          paginationContainer.innerHTML = parsed.paginationHtml;
          loadingNext = false;  // fresh grid — let the new sentinel fetch again
          armSentinel();
        }

        var countSpan = document.querySelector('[data-product-count]');
        if (countSpan && parsed.productCount !== null) {
          countSpan.textContent = parsed.productCount;
        }

        // Sync the drawer checkboxes to match the new URL — critical so
        // "Limpar tudo" actually un-checks the sidebar inputs.
        syncFormFromUrl();
        renderChips();
        updateActiveFilterDot();

        // Page height changed — re-evaluate pill visibility.
        recalcPill();
      })
      .catch(function () { grid.dataset.loading = 'false'; });
  }

  function navigateTo(url) {
    history.pushState({}, '', url);
    refreshGrid(url);
  }

  /* ─── Filter form: apply + clear ──────────────────────────────────── */
  if (filterForm) {
    filterForm.addEventListener('submit', function (e) {
      e.preventDefault();
      closeDrawer(drawerFilter);
      navigateTo(buildFilterUrl());
    });

    var clearBtn = filterForm.querySelector('[data-filter-clear]');
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        filterForm.querySelectorAll('[data-filter-input]').forEach(function (input) {
          if (input.type === 'checkbox') input.checked = false;
        });
      });
    }
  }

  /* ─── Sort form: auto-apply ────────────────────────────────────────── */
  if (sortForm) {
    sortForm.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || input.type !== 'radio') return;
      closeDrawer(drawerSort);
      navigateTo(buildSortUrl(input.value));
    });
  }

  /* ─── Sync form inputs from URL state ──────────────────────────────── */
  function syncFormFromUrl() {
    if (!filterForm) return;
    var params = new URLSearchParams(window.location.search);
    filterForm.querySelectorAll('[data-filter-input]').forEach(function (input) {
      if (input.type === 'checkbox') {
        input.checked = params.getAll(input.name).indexOf(input.value) >= 0;
      } else if (input.type === 'number') {
        input.value = params.get(input.name) || '';
      }
    });
    if (sortForm) {
      var sortValue = params.get('sort_by') || 'manual';
      sortForm.querySelectorAll('input[type="radio"]').forEach(function (r) {
        r.checked = r.value === sortValue;
      });
    }
  }

  /* ─── Active filter chips ──────────────────────────────────────────── */
  var chipsContainer = document.querySelector('[data-active-chips]');

  function tagLabel(tag) {
    switch (tag) {
      case 'tshirt':   return 'T-Shirts';
      case 'hoodie':   return 'Hoodies';
      case 'crewneck': return 'Crewnecks';
      case 'polo':     return 'Polos';
      default:         return tag;
    }
  }

  /* Maps the URL sort_by value to its drawer label so we can render
     the active sort as a removable chip alongside the filter chips. */
  var SORT_LABELS = {
    'price-ascending':    'Price: low to high',
    'price-descending':   'Price: high to low',
    'created-descending': 'Newest'
  };

  function removeSort() {
    var params = new URLSearchParams(window.location.search);
    params.delete('sort_by');
    var qs = params.toString();
    navigateTo(collectionBaseUrl + (qs ? '?' + qs : ''));
  }

  function makeChip(label, onRemove) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'collection-chip';

    var l = document.createElement('span');
    l.textContent = label;
    btn.appendChild(l);

    var x = document.createElement('span');
    x.className = 'collection-chip__remove';
    x.textContent = '×';
    btn.appendChild(x);

    btn.addEventListener('click', onRemove);
    return btn;
  }

  function removeFilter(name, value) {
    var params = new URLSearchParams(window.location.search);
    if (value === null) {
      params.delete(name);
    } else {
      var values = params.getAll(name).filter(function (v) { return v !== value; });
      params.delete(name);
      values.forEach(function (v) { params.append(name, v); });
    }
    var qs = params.toString();
    navigateTo(collectionBaseUrl + (qs ? '?' + qs : ''));
  }

  function clearAllFilters() {
    var params = new URLSearchParams(window.location.search);
    FILTER_KEYS.forEach(function (k) { params.delete(k); });
    params.delete('sort_by');
    var qs = params.toString();
    navigateTo(collectionBaseUrl + (qs ? '?' + qs : ''));
  }

  function renderChips() {
    if (!chipsContainer) return;
    chipsContainer.innerHTML = '';
    var params = new URLSearchParams(window.location.search);
    var hasAny = false;

    /* Active sort chip first so it reads as a separate concept from the
       filters. Removing this chip resets sort to the default ("Em destaque"). */
    var sortVal = params.get('sort_by');
    if (sortVal && sortVal !== 'manual' && SORT_LABELS[sortVal]) {
      hasAny = true;
      chipsContainer.appendChild(makeChip(SORT_LABELS[sortVal], removeSort));
    }

    params.getAll('filter.p.tag').forEach(function (val) {
      hasAny = true;
      chipsContainer.appendChild(makeChip(tagLabel(val), function () {
        removeFilter('filter.p.tag', val);
      }));
    });
    params.getAll('filter.v.option.color').forEach(function (val) {
      hasAny = true;
      chipsContainer.appendChild(makeChip(val, function () {
        removeFilter('filter.v.option.color', val);
      }));
    });
    if (params.get('filter.v.availability') === '1') {
      hasAny = true;
      chipsContainer.appendChild(makeChip('In stock', function () {
        removeFilter('filter.v.availability', null);
      }));
    }

    if (hasAny) {
      var clearAll = document.createElement('button');
      clearAll.type = 'button';
      clearAll.className = 'collection-chips__clear-all';
      clearAll.textContent = 'Clear all';
      clearAll.addEventListener('click', clearAllFilters);
      chipsContainer.appendChild(clearAll);
      chipsContainer.removeAttribute('hidden');
    } else {
      chipsContainer.setAttribute('hidden', '');
    }
  }

  function updateActiveFilterDot() {
    var dot = document.querySelector('[data-active-filter-dot]');
    if (!dot) return;
    var params = new URLSearchParams(window.location.search);
    var has = FILTER_KEYS.some(function (k) {
      return params.getAll(k).some(function (v) { return v && v.length > 0; });
    });
    if (has) dot.removeAttribute('hidden');
    else dot.setAttribute('hidden', '');
  }

  /* ─── Browser back/forward ─────────────────────────────────────────── */
  window.addEventListener('popstate', function () {
    syncFormFromUrl();
    refreshGrid(window.location.href);
  });

  /* ─── Initial sync on load ─────────────────────────────────────────── */
  syncFormFromUrl();
  renderChips();
  updateActiveFilterDot();
})();
